<template>
  <div class="speaker-layout">

    <div class="cutdown-wrap">
      <v-speed-dial
        v-model="speedDialOpen"
        location="top center"
        transition="slide-y-reverse-transition"
        class="cutdown-speed-dial"
      >
        <template #activator="{ props: speedDialActivatorProps }">
          <v-progress-circular
            v-bind="speedDialActivatorProps"
            class="cutdown-activator"
            :model-value="progressValue"
            :size="45"
            :width="6"
            color="blue"
          >
            {{ countdown }}
          </v-progress-circular>
        </template>
        <v-btn
          v-for="opt in speakerRefreshOptions"
          :key="opt"
          icon
          size="default"
          color="primary"
          elevation="2"
          :variant="opt === refreshIntervalSec ? 'flat' : 'elevated'"
          class="cutdown-interval-btn"
          @click="applySpeakerRefreshInterval(opt)"
        >
          <span class="cutdown-interval-sec">{{ opt }}</span>
        </v-btn>
      </v-speed-dial>
    </div>

    <v-btn
      color="primary"
      icon
      size="small"
      class="speaker-off-btn"
      @click="$emit('exit-speaker-view')"
      elevation="4"
    >
      <v-icon>mdi-account-voice-off</v-icon>
    </v-btn>

    <v-progress-linear indeterminate color="blue" v-if="isLoadingResults" />
    <div v-else-if="results.length === 0" class="text-center my-4">
      <p>{{ $t('live_results.no_results') }}</p>
    </div>
    <div v-else class="pb-1"></div>

    <!-- Floating category buttons (right edge; hidden on mobile when viewing recent) -->
    <div
      v-if="sortedCategoryNames.length > 0 && results.length > 0 && (display.mdAndUp || mobileView === 'categories')"
      class="cat-floating"
    >
      <v-btn
        v-for="catName in sortedCategoryNames"
        :key="catName"
        size="x-small"
        color="primary"
        variant="tonal"
        class="cat-float-btn"
        :title="String(catName)"
        @click="scrollToCategory(catName)"
      >
        {{ String(catName).slice(0, 4) }}
      </v-btn>
    </div>

    <!-- Swipe indicator bar (fixed under navbar) -->
    <div v-if="!display.mdAndUp && results.length > 0" class="swipe-indicator">
      <div class="swipe-bar" :class="mobileView === 'categories' ? 'swipe-left' : 'swipe-right'"></div>
    </div>

    <!-- Mobile view toggle -->
    <div v-if="!display.mdAndUp && results.length > 0" class="mobile-header">
      <v-btn-toggle v-model="mobileView" mandatory density="compact" color="primary" class="mobile-toggle">
        <v-btn value="categories" class="flex-grow-1">{{ $t('live_results.speaker_categories') }}</v-btn>
        <v-btn value="recent" class="flex-grow-1">{{ $t('live_results.speaker_recent') }}</v-btn>
      </v-btn-toggle>
    </div>

    <!-- Main two-panel layout -->
    <div
      class="speaker-panels"
      v-if="results.length > 0"
      @touchstart="onTouchStart"
      @touchend="onTouchEnd"
    >
      <!-- LEFT: Recent results -->
      <div
        class="panel-left"
        v-show="display.mdAndUp || mobileView === 'recent'"
      >
        <v-card-title class="panel-title">{{ $t('live_results.speaker_recent_title') }} ({{ recentResults.length }})</v-card-title>
        <div class="panel-scroll">
          <v-data-table
            :headers="recentHeaders"
            :items="recentResults"
            :items-per-page="-1"
            class="elevation-2"
            density="comfortable"
            striped="odd"
          >
            <template v-slot:item="{ item, index }">
                <tr
                  :class="[
                    index % 2 === 1 ? 'striped-odd' : '',
                    { 'recent-row': item.isRecent },
                  ]"
                  @click="goToCompetitor(item)"
                  style="cursor: pointer"
                >
                <td class="text-center font-weight-bold">{{ formatResultPlace(item.place, t, te) }}</td>
                <td>{{ item.last_name }} {{ item.first_name }}</td>
                <td>{{ (item.competitor_index || '').substring(0, 3) }}</td>
                <td>{{ item.competitor_category_name }}</td>
                <td :class="item.run_time ? 'font-weight-bold' : 'text-grey'">
                  {{ speakerTimeCell(item) }}
                </td>
                <td class="text-center">{{ item.punch_count }}</td>
                <!-- Desktop: splits as extra column -->
                <td v-if="display.mdAndUp" class="splits-cell">
                  <span
                    v-for="(punch, pi) in item.punches"
                    :key="pi"
                    class="mr-2"
                    :class="{ 'text-orange-darken-3': ['AP', 'DP'].includes((punch.punch_status || '').toUpperCase()) }"
                  ><b>{{ punch.code }}</b> {{ punch.cumulative_time }}<i>{{ recentPunchRankLabel(item, pi) }}</i></span>
                </td>
              </tr>
              <!-- Mobile: splits as second row -->
              <tr v-if="!display.mdAndUp && item.punches && item.punches.length" class="splits-row" :class="index % 2 === 1 ? 'striped-odd' : ''">
                <td :colspan="6" class="pa-1">
                  <div class="d-flex flex-wrap">
                    <span
                      v-for="(punch, pi) in item.punches"
                      :key="pi"
                      class="mr-2 splits-mobile-item"
                      :class="{ 'text-orange-darken-3': ['AP', 'DP'].includes((punch.punch_status || '').toUpperCase()) }"
                    ><b>{{ punch.code }}</b> {{ punch.cumulative_time }}<i>{{ recentPunchRankLabel(item, pi) }}</i></span>
                  </div>
                </td>
              </tr>
            </template>
            <template v-slot:bottom></template>
          </v-data-table>
        </div>
      </div>

      <!-- RIGHT: Category tables -->
      <div
        class="panel-right"
        v-show="display.mdAndUp || mobileView === 'categories'"
      >
        <div class="panel-scroll" ref="categoriesPanel">
          <div
            v-for="(categoryResults, categoryName) in sortedResultsByCategory"
            :key="categoryName"
            :id="`speaker-cat-${String(categoryName).replace(/\s+/g, '-')}`"
            class="mb-4"
          >
            <v-row class="mx-0">
              <v-card-title class="mb-2">{{ categoryName }} ({{ categoryResults.length }})</v-card-title>
              <v-spacer />
              <v-card-subtitle class="d-flex justify-center align-center">
                <strong class="mr-1">{{ $t('live_results.controls_label') }}</strong>
                {{ categories.find(c => c.category_name === categoryName)?.category_control_points || "" }}
              </v-card-subtitle>
            </v-row>
            <v-data-table
              :headers="categoryHeaders"
              :items="categoryResults"
              :items-per-page="-1"
              class="elevation-2"
              density="comfortable"
              hover
            >
              <template v-slot:item="{ item }">
                <tr
                  :id="`speaker-comp-${item.competitor_id}`"
                  :class="{
                    'recent-row': item.isRecent,
                    'speaker-focus-row': focusedCompetitorId === item.competitor_id,
                    'speaker-focus-fading': focusedCompetitorId === item.competitor_id && focusFading,
                  }"
                  @click="toggleSplitsTooltip(item.competitor_id)"
                  style="cursor: pointer"
                >
                  <td class="text-center">{{ formatResultPlace(item.place, t, te) }}</td>
                  <td>
                    <CountryFlags :countryCode="item.country" v-if="display.mdAndUp" />
                    {{ item.last_name }} {{ item.first_name }}
                  </td>
                  <td>{{ (item.competitor_index || '').substring(0, 3) }}</td>
                  <td v-if="hasStartTimes && item.competitor_start_time">{{ $formatTimeSec(item.competitor_start_time) }}</td>
                  <td v-if="hasStartTimes && !item.competitor_start_time"></td>
                  <td :class="item.run_time ? 'font-weight-bold' : 'text-grey'">
                    {{ speakerTimeCell(item) }}
                    <span class="lost_time" v-if="item.lost_time">+{{ item.lost_time }}</span>
                  </td>
                  <td class="text-center">{{ item.punch_count }}</td>
                </tr>
                <tr v-if="isSplitsExpanded(item.competitor_id) && item.punches && item.punches.length" class="splits-tooltip-row">
                  <td :colspan="hasStartTimes ? 7 : 6" class="pa-2">
                    <div class="d-flex flex-wrap">
                      <span
                        v-for="(punch, pi) in item.punches"
                        :key="pi"
                        class="mr-3 splits-tooltip-item"
                        :class="{ 'text-orange-darken-3': ['AP', 'DP'].includes((punch.punch_status || '').toUpperCase()) }"
                      ><b>{{ punch.code }}</b> {{ punch.cumulative_time }}<i>{{ recentPunchRankLabel(item, pi) }}</i></span>
                    </div>
                  </td>
                </tr>
              </template>
              <template v-slot:bottom></template>
            </v-data-table>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>


<script setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue';
import apiClient from '/api.js';
import { useDisplay } from 'vuetify';
import CountryFlags from '@/components/shared/CountryFlags.vue';
import { useI18n } from 'vue-i18n';
import { formatResultPlace } from '@/composables/useResultPlaceDisplay';

const display = ref(useDisplay());
const { t, te } = useI18n();

const props = defineProps({
  raceId: { type: Number, required: true },
  code: { type: String, default: '' },
  otpAttemptId: { type: Number, default: 0 },
});

const emit = defineEmits(['restrict-change', 'exit-speaker-view']);

const results = ref([]);
const categories = ref([]);
const isLoadingResults = ref(false);
const mobileView = ref('categories');
const mobileScrollPositions = { categories: 0, recent: 0 };
const categoriesPanel = ref(null);
const expandedSplitsIds = ref(new Set());
const focusedCompetitorId = ref(null);
const focusFading = ref(false);
let focusHoldTimeout = null;
let focusClearTimeout = null;

const FOCUS_HOLD_MS = 5000;
const FOCUS_FADE_MS = 2000;

const clearFocusTimers = () => {
  if (focusHoldTimeout) {
    clearTimeout(focusHoldTimeout);
    focusHoldTimeout = null;
  }
  if (focusClearTimeout) {
    clearTimeout(focusClearTimeout);
    focusClearTimeout = null;
  }
};

const scheduleFocusClear = () => {
  clearFocusTimers();
  focusFading.value = false;
  focusHoldTimeout = setTimeout(() => {
    focusFading.value = true;
    focusClearTimeout = setTimeout(() => {
      focusedCompetitorId.value = null;
      focusFading.value = false;
      focusHoldTimeout = null;
      focusClearTimeout = null;
    }, FOCUS_FADE_MS);
  }, FOCUS_HOLD_MS);
};

watch(mobileView, (newView, oldView) => {
  if (display.value.mdAndUp) return;
  mobileScrollPositions[oldView] = window.scrollY;
  nextTick(() => {
    window.scrollTo({ top: mobileScrollPositions[newView], behavior: 'instant' });
  });
});

const isSplitsExpanded = (competitorId) => expandedSplitsIds.value.has(competitorId);

const toggleSplitsTooltip = (competitorId) => {
  const next = new Set(expandedSplitsIds.value);
  if (next.has(competitorId)) next.delete(competitorId);
  else next.add(competitorId);
  expandedSplitsIds.value = next;
};

const hasStartTimes = computed(() =>
  results.value.some((item) => item.competitor_start_time)
);

const categoryHeaders = computed(() => {
  const headers = [
    { title: t('live_results.headers_place'), value: "place", width: "20px", sortable: false },
    { title: t('live_results.headers_name'), value: "last_name", sortable: false },
    { title: t('live_results.headers_club'), value: "competitor_index", sortable: false },
    { title: t('live_results.headers_time'), value: "run_time", sortable: false },
    { title: t('live_results.headers_fox'), value: "punch_count", width: "50px", align: "center", sortable: false },
  ];
  if (hasStartTimes.value) {
    headers.splice(3, 0, {
      title: t('live_results.headers_start'),
      value: "competitor_start_time",
      sortable: false,
    });
  }
  return headers;
});

const recentHeaders = computed(() => {
  const h = [
    { title: t('live_results.headers_place'), value: "place", width: "20px", sortable: false },
    { title: t('live_results.headers_name'), value: "last_name", sortable: false },
    { title: t('live_results.headers_club'), value: "competitor_index", sortable: false },
    { title: t('live_results.headers_category_short'), value: "competitor_category_name", sortable: false },
    { title: t('live_results.headers_time'), value: "run_time", sortable: false },
    { title: t('live_results.headers_fox'), value: "punch_count", width: "50px", align: "center", sortable: false },
  ];
  if (display.value.mdAndUp) {
    h.push({ title: t('live_results.headers_splits'), value: "punches", sortable: false });
  }
  return h;
});

// --- Swipe detection (mobile) ---
let touchStartX = 0;
let touchStartY = 0;
const SWIPE_THRESHOLD = 50;

const onTouchStart = (e) => {
  if (display.value.mdAndUp) return;
  const touch = e.changedTouches[0];
  touchStartX = touch.clientX;
  touchStartY = touch.clientY;
};

const onTouchEnd = (e) => {
  if (display.value.mdAndUp) return;
  const touch = e.changedTouches[0];
  const dx = touch.clientX - touchStartX;
  const dy = touch.clientY - touchStartY;
  if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;
  if (dx < 0 && mobileView.value === 'categories') {
    mobileView.value = 'recent';
  } else if (dx > 0 && mobileView.value === 'recent') {
    mobileView.value = 'categories';
  }
};

const recentResults = computed(() => {
  if (!results.value.length) return [];
  return [...results.value]
    .filter(r => r.result_timestamp)
    .sort((a, b) => {
      const ta = new Date(a.result_timestamp + 'Z').getTime();
      const tb = new Date(b.result_timestamp + 'Z').getTime();
      return tb - ta;
    })
    .slice(0, 30);
});

/** MM:SS nebo H:MM:SS → sekundy (mezičasy, cíl, `dynamic_time`). */
const parseColonTimeToSeconds = (raw) => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (s === '--:--') return null;
  const parts = s.split(':').map((x) => parseInt(x, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
};

const isPunchStatusOk = (punch) => (punch?.punch_status || '').toString().toUpperCase() === 'OK';

/** Stejný klíč jako u `recentPunchRankLabel` / výpočtu mapy OK mezičasů. */
const okPunchRankMapKey = (catName, competitorId, okOrdinal) =>
  `${catName}\u001f${competitorId}\u001f${okOrdinal}`;

/** Per category, per k-th OK punch only: rank cumulative_time among competitors who have at least k+1 OK punches (non-OK do not shift comparison index). */
const punchRanksByCategoryCompIndex = computed(() => {
  const out = new Map();
  const list = results.value;
  if (!list || list.length === 0) return out;

  const grouped = {};
  list.forEach((r) => {
    const cat = r.competitor_category_name || t('live_results.unassigned');
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  for (const [catName, rows] of Object.entries(grouped)) {
    const maxOkLen = rows.reduce((m, r) => {
      const okOnly = (r.punches || []).filter(isPunchStatusOk);
      return Math.max(m, okOnly.length);
    }, 0);

    for (let k = 0; k < maxOkLen; k++) {
      const entries = [];
      for (const r of rows) {
        const okPunches = (r.punches || []).filter(isPunchStatusOk);
        if (k >= okPunches.length) continue;
        const sec = parseColonTimeToSeconds(okPunches[k].cumulative_time);
        if (sec === null) continue;
        entries.push({ id: r.competitor_id, sec });
      }
      entries.sort((a, b) => a.sec - b.sec);
      let currentRank = 1;
      for (let j = 0; j < entries.length; j++) {
        if (j > 0 && entries[j].sec !== entries[j - 1].sec) currentRank = j + 1;
        out.set(okPunchRankMapKey(catName, entries[j].id, k), currentRank);
      }
    }
  }
  return out;
});

/**
 * Pořadí v závorce u posledního OK mezičasu (od konce `punches`), stejná logika jako v tabulce mezičasů.
 * Při shodě pak řazení podle času (`speakerTimeSortSec`).
 */
const lastOkPunchRankForSpeakerSort = (r, catName, okPunchRankMap) => {
  const punches = r.punches || [];
  for (let pi = punches.length - 1; pi >= 0; pi--) {
    if (!isPunchStatusOk(punches[pi])) continue;
    const okOrdinal = punches.slice(0, pi).filter(isPunchStatusOk).length;
    return okPunchRankMap.get(okPunchRankMapKey(catName, r.competitor_id, okOrdinal)) ?? null;
  }
  return null;
};

/** Stejná hranice jako u `ResultsTable.vue`: vyplněný result_status = dokončený řádek. */
const hasFilledResultStatus = (item) =>
  item.result_status !== null &&
  item.result_status !== undefined &&
  item.result_status !== '';

/** Sekundy od startu pro běžící závodníky (null = neplatí / jiný den / chybí start). */
const runningElapsedSec = (item) => {
  if (item.run_time) return null;
  if (!item.competitor_start_time) return null;
  const startTime = new Date(item.competitor_start_time);
  const currentTime = new Date();
  if (startTime.toISOString().slice(0, 10) !== currentTime.toISOString().slice(0, 10)) return null;
  return Math.floor((currentTime.getTime() - startTime.getTime()) / 1000);
};

const formatElapsedMmSs = (elapsedSeconds) => {
  if (elapsedSeconds < 0) return '--:--';
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/** Text času pro interval / řazení (jako `run_time` v `ResultsTable.vue`). */
const run_time = (item) => {
  if (hasFilledResultStatus(item)) return item.run_time || '';
  if (item.run_time) return item.run_time;
  if (!item.competitor_start_time) return '';
  const sec = runningElapsedSec(item);
  if (sec === null) return '';
  return formatElapsedMmSs(sec);
};

/**
 * Zobrazení v tabulce: u běžících musí šablona číst `item.dynamic_time` (doplňuje interval),
 * jinak Vuetify / Vue neinvaliduje buňku jen kvůli `Date` uvnitř `run_time()`.
 */
const speakerTimeCell = (item) => {
  if (hasFilledResultStatus(item)) return item.run_time || '';
  if (item.run_time) return item.run_time;
  if (!item.competitor_start_time) return '';
  const d = item.dynamic_time;
  return d != null && d !== '' ? d : run_time(item);
};

const finishSecFromRunTime = (item) => parseColonTimeToSeconds(item?.run_time);

/** Řazení: cíl → uběhlé sekundy od startu → `dynamic_time` z intervalu. */
const speakerTimeSortSec = (item) => {
  const fin = finishSecFromRunTime(item);
  if (fin != null) return fin;
  const run = runningElapsedSec(item);
  if (run != null && run >= 0) return run;
  return parseColonTimeToSeconds(item.dynamic_time);
};

const isNullFoxPunchCount = (x) => x.punch_count == null || x.punch_count === '';

const numericPlaceSpeaker = (x) => {
  if (x.place === null || x.place === '') return null;
  const n = Number(x.place);
  return Number.isNaN(n) ? null : n;
};

/** Diskvalifikace / ne-OK status — vždy až za běžící i za oficiální. */
const isSpeakerNonOkStatusTail = (item) => {
  if (!hasFilledResultStatus(item)) return false;
  const u = String(item.result_status).trim().toUpperCase();
  return u !== 'OK';
};

/**
 * Řazení „živých“ řádků: poslední OK punch, čas, start / místo jako dřív.
 */
const compareSpeakerLivePending = (a, b, catName, okPunchRankMap) => {
  const ra = lastOkPunchRankForSpeakerSort(a, catName, okPunchRankMap) ?? 99999;
  const rb = lastOkPunchRankForSpeakerSort(b, catName, okPunchRankMap) ?? 99999;
  if (ra !== rb) return ra - rb;

  const ta = speakerTimeSortSec(a);
  const tb = speakerTimeSortSec(b);
  const bothNullFox = isNullFoxPunchCount(a) && isNullFoxPunchCount(b);
  if (ta != null && tb != null && ta !== tb) {
    return bothNullFox ? tb - ta : ta - tb;
  }
  if (ta != null && tb == null) return -1;
  if (ta == null && tb != null) return 1;

  const aNoStart = !a.competitor_start_time;
  const bNoStart = !b.competitor_start_time;
  if (aNoStart || bNoStart) {
    const pa = numericPlaceSpeaker(a) ?? 99999;
    const pb = numericPlaceSpeaker(b) ?? 99999;
    if (pa !== pb) return pa - pb;
  }
  if (a.competitor_start_time && b.competitor_start_time) {
    return new Date(a.competitor_start_time) - new Date(b.competitor_start_time);
  }
  if (a.competitor_start_time && !b.competitor_start_time) return -1;
  if (!a.competitor_start_time && b.competitor_start_time) return 1;
  return 0;
};

/** Má záznam mezičasy v `punches` (bez toho nelze řadit podle punch ranku). */
const hasSpeakerSplitTimes = (item) => ((item.punches || []).length > 0);

/** Konečný výsledek: OK status + číselné místo (ne tail). */
const isSpeakerFinalOkWithNumericPlace = (item) =>
  !isSpeakerNonOkStatusTail(item) &&
  hasFilledResultStatus(item) &&
  String(item.result_status).trim().toUpperCase() === 'OK' &&
  numericPlaceSpeaker(item) != null;

/**
 * Řádky bez mezičasů — až za skupinu s mezičasy; čas sestupně.
 */
const compareSpeakerNoSplitsDescending = (a, b) => {
  const ta = speakerTimeSortSec(a);
  const tb = speakerTimeSortSec(b);
  if (ta != null && tb != null && ta !== tb) return tb - ta;
  if (ta != null && tb == null) return -1;
  if (ta == null && tb != null) return 1;
  if (a.competitor_start_time && b.competitor_start_time) {
    return new Date(a.competitor_start_time) - new Date(b.competitor_start_time);
  }
  if (a.competitor_start_time && !b.competitor_start_time) return -1;
  if (!a.competitor_start_time && b.competitor_start_time) return 1;
  return 0;
};

/**
 * S mezičasy: dva koneční (OK + místo) řadíme jen podle place vzestupně.
 * Jinak (živí vs. koneční vs. OK bez místa) — nejdřív pořadí na posledním OK punchu vůči ostatním,
 * pak stejné doplnění jako u živých (čas, …).
 */
const compareSpeakerWithSplitsInterleaved = (a, b, catName, okPunchRankMap) => {
  if (isSpeakerFinalOkWithNumericPlace(a) && isSpeakerFinalOkWithNumericPlace(b)) {
    return numericPlaceSpeaker(a) - numericPlaceSpeaker(b);
  }
  const ra = lastOkPunchRankForSpeakerSort(a, catName, okPunchRankMap) ?? 99999;
  const rb = lastOkPunchRankForSpeakerSort(b, catName, okPunchRankMap) ?? 99999;
  if (ra !== rb) return ra - rb;
  return compareSpeakerLivePending(a, b, catName, okPunchRankMap);
};

const compareSpeakerCategoryRow = (a, b, catName, okPunchRankMap) => {
  const tailA = isSpeakerNonOkStatusTail(a);
  const tailB = isSpeakerNonOkStatusTail(b);
  if (tailA !== tailB) return tailA ? 1 : -1;

  const splitA = hasSpeakerSplitTimes(a);
  const splitB = hasSpeakerSplitTimes(b);
  if (splitA !== splitB) return splitA ? -1 : 1;

  if (!splitA) return compareSpeakerNoSplitsDescending(a, b);

  return compareSpeakerWithSplitsInterleaved(a, b, catName, okPunchRankMap);
};

const sortedResultsByCategory = computed(() => {
  if (!results.value.length) return {};
  const okPunchRankMap = punchRanksByCategoryCompIndex.value;
  const grouped = {};
  results.value.forEach((r) => {
    const cat = r.competitor_category_name || t('live_results.unassigned');
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });
  const sortedKeys = Object.keys(grouped).sort();
  const out = {};
  sortedKeys.forEach((cat) => {
    out[cat] = [...grouped[cat]].sort((a, b) => compareSpeakerCategoryRow(a, b, cat, okPunchRankMap));
  });
  return out;
});

const sortedCategoryNames = computed(() => Object.keys(sortedResultsByCategory.value));

const recentPunchRankLabel = (item, punchIndex) => {
  const punches = item.punches || [];
  const punch = punches[punchIndex];
  if (!punch || !isPunchStatusOk(punch)) return '';
  const okOrdinal = punches.slice(0, punchIndex).filter(isPunchStatusOk).length;
  const cat = item.competitor_category_name || t('live_results.unassigned');
  const key = okPunchRankMapKey(cat, item.competitor_id, okOrdinal);
  const rank = punchRanksByCategoryCompIndex.value.get(key);
  return rank == null ? '' : ` (${rank})`;
};

const goToCompetitor = (item) => {
  focusedCompetitorId.value = item.competitor_id ?? null;
  scheduleFocusClear();
  const catName = item.competitor_category_name;
  if (!display.value.mdAndUp) {
    mobileView.value = 'categories';
    setTimeout(() => scrollToCompetitor(item.competitor_id, catName), 100);
  } else {
    scrollToCompetitor(item.competitor_id, catName);
  }
};

const NAVBAR_OFFSET = 60;

const scrollToCategory = (catName) => {
  const id = `speaker-cat-${String(catName).replace(/\s+/g, '-')}`;
  const el = document.getElementById(id);
  if (!el) return;
  const container = categoriesPanel.value;
  if (container && display.value.mdAndUp) {
    container.scrollTo({
      top: el.offsetTop - container.offsetTop,
      behavior: 'instant',
    });
  } else {
    const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
    window.scrollTo({ top, behavior: 'instant' });
  }
};

/** Scroll tak, aby řádek závodníka byl zhruba uprostřed panelu / obrazovky. */
const scrollToCompetitor = (competitorId, catName) => {
  nextTick(() => {
    const el = document.getElementById(`speaker-comp-${competitorId}`);
    if (!el) {
      if (catName) scrollToCategory(catName);
      return;
    }

    const container = categoriesPanel.value;
    if (container && display.value.mdAndUp) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const elOffset = elRect.top - containerRect.top + container.scrollTop;
      const target = elOffset - container.clientHeight / 2 + el.offsetHeight / 2;
      container.scrollTo({ top: Math.max(0, target), behavior: 'instant' });
    } else {
      const rect = el.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const target = absoluteTop - window.innerHeight / 2 + rect.height / 2;
      window.scrollTo({ top: Math.max(0, target), behavior: 'instant' });
    }
  });
};

const refreshSpeakerDynamicTimes = () => {
  results.value.forEach((item) => {
    if (!item.run_time && item.competitor_start_time) {
      item.dynamic_time = run_time(item);
    }
  });
};

// --- Data fetching ---
const fetchResults = async ({ fromOtpAttempt = false } = {}) => {
  if (!props.raceId) return;
  isLoadingResults.value = true;
  try {
    const raw = (props.code || '').trim();
    const codeParam = raw.length === 6 ? raw : '';
    const url = codeParam
      ? `/results/?race_id=${props.raceId}&all=true&code=${encodeURIComponent(codeParam)}`
      : `/results/?race_id=${props.raceId}&all=true`;

    const response = await apiClient.get(url);

    categories.value = response.data.categories || [];
    results.value = (response.data.results || []).map((item) => {
      if (item.result_timestamp) {
        const utc = new Date(item.result_timestamp + 'Z');
        item.isRecent = Date.now() - utc.getTime() < 4 * 60 * 1000;
      } else {
        item.isRecent = false;
      }
      return item;
    });
    refreshSpeakerDynamicTimes();
    const restrict = !!response.data.results_restrict;
    emit('restrict-change', {
      restrict,
      invalidCode: fromOtpAttempt && codeParam.length === 6 && restrict,
      fromOtpAttempt,
    });
  } catch (error) {
    console.error("Chyba při načítání dat:", error);
    results.value = [];
    categories.value = [];
  } finally {
    isLoadingResults.value = false;
  }
};

// --- Auto refresh (interval v s, výchozí 10; volba 10 / 5 / 2 v speed dial + localStorage) ---
const SPEAKER_REFRESH_LS = 'speakerResultsRefreshSeconds';
const speakerRefreshOptions = [10, 5, 2];

const loadSpeakerRefreshSeconds = () => {
  try {
    const n = parseInt(localStorage.getItem(SPEAKER_REFRESH_LS), 10);
    if (Number.isFinite(n) && speakerRefreshOptions.includes(n)) return n;
  } catch {
    /* ignore */
  }
  return 10;
};

const refreshIntervalSec = ref(loadSpeakerRefreshSeconds());
const speedDialOpen = ref(false);

const applySpeakerRefreshInterval = (sec) => {
  if (!speakerRefreshOptions.includes(sec)) return;
  refreshIntervalSec.value = sec;
  localStorage.setItem(SPEAKER_REFRESH_LS, String(sec));
  speedDialOpen.value = false;
  stopAutoRefresh();
  startAutoRefresh();
};

const autoRefreshInterval = ref(null);
const countdown = ref(refreshIntervalSec.value);
const progressValue = ref(100);

const startAutoRefresh = () => {
  stopAutoRefresh();
  const sec = refreshIntervalSec.value;
  countdown.value = sec;
  progressValue.value = 100;

  const mainInterval = setInterval(() => {
    fetchResults({ fromOtpAttempt: false });
    countdown.value = sec;
    progressValue.value = 100;
  }, sec * 1000);

  const countdownInterval = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value -= 1;
      progressValue.value = sec > 0 ? (countdown.value / sec) * 100 : 0;
    }
  }, 1000);

  autoRefreshInterval.value = { interval: mainInterval, countdownInterval };
};

const stopAutoRefresh = () => {
  if (autoRefreshInterval.value) {
    clearInterval(autoRefreshInterval.value.interval);
    clearInterval(autoRefreshInterval.value.countdownInterval);
    autoRefreshInterval.value = null;
  }
};

// --- Watchers ---
watch(
  () => props.raceId,
  async (newRaceId) => {
    if (!newRaceId) return;
    await fetchResults();
  },
  { immediate: true }
);

watch(
  () => props.otpAttemptId,
  async (n, o) => {
    if (!n || n === o) return;
    await fetchResults({ fromOtpAttempt: true });
  }
);

// --- Lifecycle ---
let dynamicTimeInterval = null;

onMounted(() => {
  startAutoRefresh();
  dynamicTimeInterval = setInterval(() => {
    refreshSpeakerDynamicTimes();
  }, 1000);
});

onUnmounted(() => {
  stopAutoRefresh();
  if (dynamicTimeInterval) clearInterval(dynamicTimeInterval);
  clearFocusTimers();
});
</script>


<style scoped>
/* === Layout === */
.speaker-layout {
  position: relative;
}

/* Desktop: two panels side by side, fixed to viewport height */
@media (min-width: 960px) {
  .speaker-panels {
    display: flex;
    gap: 8px;
    padding: 0 4px;
    height: calc(100vh - 140px);
    overflow: hidden;
  }

  .panel-left {
    width: 65%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .panel-right {
    width: 35%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-right: 50px;
  }

  .panel-scroll {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
  }
}

.panel-title {
  flex-shrink: 0;
  padding: 4px 16px;
}

/* Mobile: natural page scrolling */
@media (max-width: 959px) {
  .panel-left {
    width: 100%;
  }

  .panel-right {
    width: auto;
    margin-right: 40px;
  }
}

/* === Floating category buttons (both desktop and mobile) === */
.cat-floating {
  position: fixed;
  right: 4px;
  top: 55%;
  transform: translateY(-50%);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 2px;
}

.cat-float-btn {
  width: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  height: 26px !important;
  min-height: 26px !important;
  font-size: 11px !important;
  padding: 0 !important;
  letter-spacing: 0;
  justify-content: center !important;
  text-align: center;
  overflow: hidden;
}

.cat-float-btn :deep(.v-btn__content) {
  justify-content: center;
}

/* Desktop: wider and taller category buttons */
@media (min-width: 960px) {
  .cat-float-btn {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    height: 32px !important;
    min-height: 32px !important;
    font-size: 13px !important;
    padding: 0 !important;
  }
}

/* === Mobile === */
.mobile-header {
  padding: 4px 0;
}

.mobile-toggle {
  display: flex !important;
  width: 100%;
}

.swipe-indicator {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  height: 4px;
  z-index: 1000;
}

.swipe-bar {
  position: absolute;
  top: 0;
  width: 50%;
  height: 100%;
  background-color: #ff0000;
  border-radius: 2px;
  transition: left 0.2s ease;
}

.swipe-left {
  left: 0;
}

.swipe-right {
  left: 50%;
}

/* === Common === */
.cutdown-wrap {
  position: fixed;
  bottom: 26px;
  left: 18px;
  z-index: 1000;
}

.cutdown-speed-dial {
  position: relative;
}

.cutdown-activator {
  cursor: pointer;
}

.cutdown-interval-btn .cutdown-interval-sec {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1;
}

.speaker-off-btn {
  position: fixed;
  bottom: 30px;
  left: 85px;
  z-index: 1000;
}


.recent-row {
  background-color: rgb(var(--v-theme-results-recent)) !important;
}

.speaker-focus-row {
  background-color: rgb(var(--v-theme-results-highlight)) !important;
  transition: background-color 0s;
}

.speaker-focus-row.speaker-focus-fading {
  background-color: transparent !important;
  transition: background-color 2s ease;
}

.recent-row.speaker-focus-row.speaker-focus-fading {
  background-color: rgb(var(--v-theme-results-recent)) !important;
}

.splits-tooltip-row td {
  background-color: rgb(var(--v-theme-results-highlight)) !important;
  border-bottom: 2px solid rgb(var(--v-theme-link)) !important;
}

.splits-tooltip-item {
  white-space: nowrap;
  font-size: 13px;
  line-height: 1.6;
}

.splits-cell {
  white-space: normal;
  font-size: 14px;
}

.splits-row td {
  border-bottom: 1px solid #e0e0e0 !important;
}

.splits-mobile-item {
  font-size: 13px;
  white-space: nowrap;
}

.lost_time {
  color: gray;
  font-size: 11px;
  font-weight: lighter;
}

.v-data-table > .v-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-table__wrapper > table > tbody > tr > th,
.v-data-table > .v-table__wrapper > table > thead > tr > td,
.v-data-table > .v-table__wrapper > table > thead > tr > th,
.v-data-table > .v-table__wrapper > table > tfoot > tr > td,
.v-data-table > .v-table__wrapper > table > tfoot > tr > th {
  padding: 0 8px !important;
}

:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  padding: 0 8px !important;
}

</style>
